import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "@phosphor-icons/react";
import { fetchBlobUrl, fmtDate } from "@/lib/medicoes";

export const fotoCoords = (f) => {
  const m = f.metadados_originais || {};
  if (m.gps_lat != null && m.gps_lon != null) return { lat: m.gps_lat, lon: m.gps_lon, fonte: "GPS gravado na foto (não verificado)" };
  const d = f.localizacao_dispositivo;
  if (d?.lat != null) return { lat: d.lat, lon: d.lon, fonte: `Localização do dispositivo – não verificada${d.precisao_m ? ` (±${Math.round(d.precisao_m)} m)` : ""}` };
  return null;
};

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function pinIcon(n, device) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${device ? "#F59E0B" : "#009C3B"};color:#0F172A;border:2px solid #FFDF00;border-radius:50% 50% 50% 0;transform:rotate(-45deg);width:28px;height:28px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px #0008"><span style="transform:rotate(45deg);font:bold 11px sans-serif;color:#fff">${n}</span></div>`,
    iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -26],
  });
}

export default function FotosMapa({ fotos = [], height = 360 }) {
  const ref = useRef(null);
  const pontos = fotos.map((f, i) => ({ f, i, c: fotoCoords(f) })).filter((p) => p.c);
  const semLoc = fotos.map((f, i) => ({ f, i })).filter(({ f }) => !fotoCoords(f));
  const key = pontos.map((p) => `${p.f.id}:${p.c.lat},${p.c.lon}`).join("|");

  useEffect(() => {
    if (!ref.current || !pontos.length) return undefined;
    const map = L.map(ref.current, { scrollWheelZoom: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    const urls = [];
    const bounds = [];
    pontos.forEach(({ f, i, c }) => {
      const mk = L.marker([c.lat, c.lon], { icon: pinIcon(i + 1, !(f.metadados_originais || {}).gps_lat) }).addTo(map);
      const imgId = `pop-img-${f.id}`;
      mk.bindPopup(`<div style="width:210px"><div id="${imgId}" style="width:210px;height:140px;background:#1E293B;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#94A3B8;font-size:11px">Carregando…</div>
        <div style="font-weight:600;margin-top:6px">Foto ${i + 1}${f.legenda ? ` · ${esc(f.legenda)}` : ""}</div>
        <div style="font-size:11px;color:#475569">${esc(f.data_registro ? fmtDate(f.data_registro) : fmtDate(f.created_at))}${f.local ? ` · ${esc(f.local)}` : ""}</div>
        <div style="font-size:10px;color:#64748B;margin-top:2px">${esc(c.fonte)}</div></div>`);
      mk.on("popupopen", async () => {
        const el = document.getElementById(imgId);
        if (!el || el.dataset.loaded) return;
        try {
          const u = await fetchBlobUrl(`/medicoes/arquivos/${f.id}`);
          urls.push(u);
          el.dataset.loaded = "1";
          el.innerHTML = `<img src="${u}" style="width:210px;height:140px;object-fit:cover;border-radius:4px" onerror="this.parentNode.textContent='Pré-visualização indisponível'"/>`;
        } catch { el.textContent = "Pré-visualização indisponível"; }
      });
      bounds.push([c.lat, c.lon]);
    });
    if (bounds.length === 1) map.setView(bounds[0], 17);
    else map.fitBounds(bounds, { padding: [30, 30], maxZoom: 18 });
    return () => { map.remove(); urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!fotos.length) return null;
  return (
    <div className="space-y-2" data-testid="fotos-mapa">
      <div className="flex items-center gap-2 uppercase-label !text-[10px]"><MapPin size={12} /> Mapa das fotos ({pontos.length} com localização)</div>
      {pontos.length > 0 ? (
        <div ref={ref} style={{ height }} className="rounded-lg overflow-hidden border border-[#334155] z-0 relative" data-testid="fotos-mapa-canvas" />
      ) : <p className="text-xs text-[#94A3B8]">Nenhuma foto possui localização registrada.</p>}
      <div className="flex flex-wrap gap-3 text-[10px] text-[#94A3B8]">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#009C3B]" /> GPS da foto</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Localização do dispositivo</span>
        <span>Localizações não verificadas pela fiscalização.</span>
      </div>
      {semLoc.length > 0 && (
        <div className="text-[11px] text-[#94A3B8]" data-testid="fotos-sem-localizacao">
          Sem localização: {semLoc.map(({ f, i }) => `Foto ${i + 1}${f.legenda ? ` (${f.legenda})` : ""}`).join(", ")}
        </div>
      )}
    </div>
  );
}
